
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTasks } from '@/context/TaskContext';
import { 
  createTimeEntry, 
  updateTimeEntry, 
  finalizeTimeEntry, 
  getActiveTimeEntry,
  cleanupInProgressEntries 
} from '@/context/TaskContext/timeEntriesApi';

export const useActiveTimeEntry = () => {
  const { user } = useAuth();
  const { activeTask } = useTasks();
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start a new time entry session
  const startTimeEntry = async () => {
    if (!user || !activeTask) {
      console.log('Cannot start time entry: no user or active task');
      return null;
    }

    try {
      // Cleanup any existing in-progress entries for this task
      await cleanupInProgressEntries(user.id, activeTask.id);

      // Create new in-progress entry
      const entryId = await createTimeEntry(
        activeTask.id,
        user.id,
        0, // Start with 0 duration
        'pomodoro',
        'in_progress'
      );

      if (entryId) {
        setActiveEntryId(entryId);
        const now = new Date();
        setStartTime(now);
        console.log('Started new time entry:', entryId);
        
        // Start updating every 30 seconds
        startPeriodicUpdates();
        return entryId;
      }
    } catch (error) {
      console.error('Failed to start time entry:', error);
    }
    return null;
  };

  // Update the current entry with elapsed time
  const updateCurrentEntry = async (forceUpdate = false) => {
    if (!activeEntryId || !startTime) return;

    const now = new Date();
    const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    
    // Only update if significant time has passed or forced
    if (elapsedSeconds > 10 || forceUpdate) {
      try {
        // Update the time entry in the database
        await updateTimeEntry(activeEntryId, elapsedSeconds);
        console.log(`Updated time entry: ${elapsedSeconds}s`);
      } catch (error) {
        console.error('Failed to update time entry:', error);
      }
    }
  };

  // Finalize the current entry
  const finalizeCurrentEntry = async (sessionStatus: 'completed' | 'interrupted' = 'completed') => {
    if (!activeEntryId || !startTime) return;

    const finalDuration = Math.floor((Date.now() - startTime.getTime()) / 1000);
    
    try {
      await finalizeTimeEntry(activeEntryId, finalDuration, sessionStatus);
      console.log(`Finalized time entry: ${finalDuration}s as ${sessionStatus}`);
      
      stopPeriodicUpdates();
      setActiveEntryId(null);
      setStartTime(null);
      
      return finalDuration;
    } catch (error) {
      console.error('Failed to finalize time entry:', error);
      return 0;
    }
  };

  // Start periodic updates every 30 seconds
  const startPeriodicUpdates = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    
    updateIntervalRef.current = setInterval(() => {
      updateCurrentEntry();
    }, 30000); // Update every 30 seconds
  };

  // Stop periodic updates
  const stopPeriodicUpdates = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPeriodicUpdates();
    };
  }, []);

  // Cleanup on task change
  useEffect(() => {
    if (activeEntryId && activeTask) {
      // If task changed while we have an active entry, finalize as interrupted
      const checkTaskChange = async () => {
        try {
          const activeEntry = await getActiveTimeEntry(user?.id || '', activeTask.id);
          if (!activeEntry || activeEntry.id !== activeEntryId) {
            // Current active entry is for a different task, finalize as interrupted
            await finalizeCurrentEntry('interrupted');
          }
        } catch (error) {
          console.error('Error checking task change:', error);
        }
      };
      
      checkTaskChange();
    }
  }, [activeTask?.id]);

  return {
    activeEntryId,
    isTracking: !!activeEntryId,
    startTimeEntry,
    updateCurrentEntry,
    finalizeCurrentEntry,
    getCurrentDuration: () => startTime ? Math.floor((Date.now() - startTime.getTime()) / 1000) : 0
  };
};
